import axios from 'axios';
import { DiscoveredChannelsPage, DiscoveredChannelsQuery, IDiscoveredChannelRepository } from '../../application/ports/IDiscoveredChannelRepository';
import { DiscoveredChannelEntity } from '../../domain/entities/DiscoveredChannel';
import { advertizerApiHost } from '../../constants/envs';
import { AuthTokenProvider } from '../auth/AuthTokenProvider';

const BASE_PATH = '/api/admin/discovered-channels';

type DiscoveredChannelDTO = Omit<DiscoveredChannelEntity, 'verifiedAt'> & { verifiedAt?: string | null };

const toEntity = (dto: DiscoveredChannelDTO): DiscoveredChannelEntity => ({
  ...dto,
  kind: dto.kind ?? '',
  title: dto.title ?? '',
  verifyError: dto.verifyError ?? '',
  verifiedAt: dto.verifiedAt ?? undefined,
});

export class DiscoveredChannelsAxiosRepository implements IDiscoveredChannelRepository {
  constructor(private auth: AuthTokenProvider) {}

  private async authHeaders() {
    const token = await this.auth.getToken();
    if (!token) throw new Error('Unauthenticated');
    return { Authorization: `Bearer ${token}` };
  }

  async list({ filters, page, perPage }: DiscoveredChannelsQuery): Promise<DiscoveredChannelsPage> {
    const headers = await this.authHeaders();
    const params: Record<string, string | number | boolean> = { page, perPage, sort: filters.sort };
    if (filters.statuses.length) params.status = filters.statuses.join(',');
    if (filters.kinds.length) params.kind = filters.kinds.join(',');
    if (filters.minMembers) params.minMembers = filters.minMembers;
    if (filters.search) params.search = filters.search;
    if (filters.inCatalog !== undefined) params.inCatalog = filters.inCatalog;
    const res = await axios.get(`${advertizerApiHost}${BASE_PATH}`, { headers, params });
    return { items: (res.data.items ?? []).map(toEntity), total: res.data.pagination?.total ?? 0 };
  }

  async importToCatalog(ids: number[]): Promise<string> {
    const headers = await this.authHeaders();
    const res = await axios.post(`${advertizerApiHost}${BASE_PATH}/import`, { ids }, { headers });
    return res.data.draftUuid;
  }

  async reject(ids: number[]): Promise<void> {
    const headers = await this.authHeaders();
    await axios.post(`${advertizerApiHost}${BASE_PATH}/reject`, { ids }, { headers });
  }
}
